import React, {useCallback, useEffect, useLayoutEffect, useRef, useState, type JSX} from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react';
import classnames from 'classnames';

import Text from '@albert-io/atomic/atoms/Text/Text.react';
import DropdownItem from '@albert-io/atomic/molecules/DropdownItem/DropdownItem.react';
import Field from '@albert-io/atomic/molecules/Field/Field.react';
import IconInput from '@albert-io/atomic/molecules/IconInput/IconInput.react';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';

import './search-and-select-v2.scss';

export interface SearchAndSelectV2Props<TOption> {
  /** The options to show in the dropdown. The parent is responsible for fetching/filtering them. */
  options: TOption[];
  /** Stable identity for an option (used for keys and selected-state). */
  getOptionKey: (option: TOption) => string;
  /** Display label for an option (used in the list and when `replaceOnSelect`). */
  getOptionLabel: (option: TOption) => string;
  /** Optional custom rendering for a dropdown row; defaults to the label. */
  renderOption?: (option: TOption) => React.ReactNode;
  /** Called when an option is chosen. */
  onSelect: (option: TOption) => void;
  /** Called when the input is cleared, so a consumer can drop the current selection. */
  onClear?: () => void;
  /** Called (debounced) when the search term changes — wire this to your data source. */
  onSearchChange?: (searchTerm: string) => void;
  /** Keys of already-selected options, rendered with a check and `aria-selected`. */
  selectedKeys?: string[];
  /** Shows a "Searching…" row while true. */
  loading?: boolean;

  label?: string;
  placeholder?: string;
  iconRight?: IconProp;
  error?: boolean;
  /** Helper or validation text rendered beneath the input. */
  message?: string | null | boolean;
  disabled?: boolean;

  /** Search term to start with, e.g. the label of an already-chosen option when editing. */
  initialSearchTerm?: string;

  /** Open the dropdown as soon as the input is focused (not only once typing). */
  openOnFocus?: boolean;
  /** Clear the search input after selecting. */
  clearOnSelect?: boolean;
  /** Replace the search input with the selected option's label. */
  replaceOnSelect?: boolean;
  /** Close the dropdown after selecting. */
  closeOnSelect?: boolean;

  emptyMessage?: string;
  /** Debounce before firing `onSearchChange`. */
  searchDebounceMs?: number;
  /** Footer rendered at the bottom of the dropdown (e.g. a "create new" affordance). */
  customFooterRenderer?: (args: {searchTerm: string; resetSearch: () => void}) => React.ReactNode;
  className?: string;
}

/**
 * Returns a permanently stable function that always calls the *latest* `callback`. Lets the
 * debounce effect below depend on a consumer's `onSearchChange` honestly without re-running (and
 * re-firing the search) on every render — the same idea as React's upcoming `useEffectEvent`.
 */
function useEventCallback<TArgs extends unknown[], TReturn>(
  callback: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn {
  const callbackRef = useRef(callback);
  useLayoutEffect(() => {
    callbackRef.current = callback;
  });
  return useCallback((...args: TArgs) => callbackRef.current(...args), []);
}

/**
 * # SearchAndSelectV2
 *
 * A presentational, data-source-agnostic search-and-select. Unlike the legacy
 * `SearchAndSelect` (which owns JSON:API fetching via a query builder), v2 is **controlled**:
 * the parent supplies `options` and reacts to `onSearchChange`, so it works with GraphQL
 * (or anything else). Positioned and portaled with Floating UI, so the dropdown is never
 * clipped by `overflow` containers (e.g. modal bodies).
 */
export default function SearchAndSelectV2<TOption>({
  options,
  getOptionKey,
  getOptionLabel,
  renderOption,
  onSelect,
  onClear,
  onSearchChange,
  selectedKeys = [],
  loading = false,
  label,
  placeholder = 'Type to search',
  iconRight = 'search',
  error = false,
  message,
  disabled = false,
  initialSearchTerm = '',
  openOnFocus = false,
  clearOnSelect = false,
  replaceOnSelect = false,
  closeOnSelect = false,
  emptyMessage = 'No results found',
  searchDebounceMs = 200,
  customFooterRenderer,
  className
}: SearchAndSelectV2Props<TOption>): JSX.Element {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [open, setOpen] = useState(false);

  const {refs, floatingStyles, context} = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({padding: 8}),
      shift({padding: 8}),
      size({
        padding: 8,
        apply({rects, elements}) {
          // Match the trigger width (the dropdown is portaled to <body>, so it can't use 100%).
          Object.assign(elements.floating.style, {width: `${rects.reference.width}px`});
        }
      })
    ]
  });

  // useDismiss handles outside-click + Escape; useRole wires combobox/listbox a11y attributes.
  const dismiss = useDismiss(context);
  const role = useRole(context, {role: 'listbox'});
  const {getReferenceProps, getFloatingProps} = useInteractions([dismiss, role]);

  // Stable emitter for the outward search notification. Consumers routinely pass a fresh
  // `onSearchChange` closure each render; `useEventCallback` lets the debounce effect depend on it
  // honestly without re-running (and re-firing the search) on every render.
  const emitSearch = useEventCallback((term: string) => onSearchChange?.(term));

  // Debounce the search notification — fires only when the term (or debounce) actually changes.
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return undefined;
    }
    const timer = setTimeout(() => emitSearch(searchTerm), searchDebounceMs);
    return () => clearTimeout(timer);
  }, [searchTerm, searchDebounceMs, emitSearch]);

  const resetSearch = (): void => {
    setSearchTerm('');
    setOpen(openOnFocus);
    onClear?.();
  };

  const handleChange = (value: string): void => {
    setSearchTerm(value);
    setOpen(openOnFocus ? true : value !== '');
  };

  const handleFocus = (): void => {
    if (openOnFocus || searchTerm !== '') {
      setOpen(true);
    }
  };

  const handleSelect = (option: TOption): void => {
    if (replaceOnSelect) {
      setSearchTerm(getOptionLabel(option));
    } else if (clearOnSelect) {
      resetSearch();
    }
    onSelect(option);
    if (closeOnSelect) {
      setOpen(false);
    }
  };

  const selected = new Set(selectedKeys);

  return (
    <div className={classnames('o-search-select', className)}>
      {/*
        Floating UI marks this element a `combobox` that controls the listbox. Since the
        listbox is portaled out of this subtree, also `aria-owns` it so assistive tech keeps
        the ownership relationship the portal would otherwise break.
      */}
      <div
        ref={refs.setReference}
        {...getReferenceProps({'aria-owns': open ? context.floatingId : undefined})}
      >
        <Field
          as={IconInput}
          label={label}
          aria-label={label ? undefined : placeholder}
          border
          fillWrapper
          value={searchTerm}
          disabled={disabled}
          error={error}
          message={message}
          placeholder={placeholder}
          iconRight={searchTerm ? 'times' : iconRight}
          onIconRightClick={searchTerm ? resetSearch : undefined}
          iconButtonLabel='Clear search'
          onFocus={handleFocus}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
        />
      </div>

      {open && (
        <FloatingPortal>
          <div
            // oxlint-disable-next-line react/refs -- floating-ui's refs.setFloating is a stable callback ref, not a render-time ref read
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps({'aria-label': label ?? placeholder})}
            className='o-search-select-v2__dropdown elevation-shadow-l3'
          >
            <div className='o-search-select-v2__content rounded-x1 border-border-primary background-primary border border-solid'>
              {loading && (
                <Text as='div' className='p-x2!' color='tertiary'>
                  Searching…
                </Text>
              )}

              {!loading && options.length === 0 && (
                <Text as='div' className='p-x2!' color='tertiary'>
                  {emptyMessage}
                </Text>
              )}

              {!loading &&
                options.map((option) => {
                  const key = getOptionKey(option);
                  const isSelected = selected.has(key);
                  return (
                    <DropdownItem
                      key={key}
                      role='option'
                      aria-selected={isSelected}
                      tabIndex={0}
                      className='o-search-select__item'
                      icon={isSelected ? 'check' : undefined}
                      onClick={() => handleSelect(option)}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelect(option);
                        }
                      }}
                    >
                      {renderOption ? renderOption(option) : getOptionLabel(option)}
                    </DropdownItem>
                  );
                })}

              {customFooterRenderer?.({searchTerm, resetSearch})}
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}
